import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { base64FileDownload } from "../../utils/FileDownloadUtil";
import HasPermissionTo from "../../common/HasPermissionTo";
import { withTranslation } from "react-i18next";
import NscBusinessSettings from "./NscBusinessSettings";
import DealerBusinessSettings from "./DealerBusinessSettings";
import TermsAndConditionsClient from "../../shared/clients/TermsAndConditionsClient";
import PendingActionClient from "../../shared/clients/PendingActionClient";
import {
  CUPID_ID,
  DELIVERY_BANDS,
  LEAD_TIMES,
  PAYMENT_THRESHOLD,
  TERMS_AND_CONDITIONS,
} from "../../common/Constants";

function BusinessSettings(props) {
  const [isDealerAcceptingTerms, setIsDealerAcceptingTerms] = useState(false);
  const [acceptTermsAndConditions, setAcceptTermsAndConditions] = useState({});
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [termsAndConditionsHistory, setTermsAndConditionsHistory] = useState([]);
  const [enableUpload, setEnableUpload] = useState(undefined);
  const [restrictDropdown, setRestrictDropdown] = useState(false);

  const pendingAction = props.location && props.location.query && props.location.query.fromPendingActions;

  const initialTab = useMemo(() => {
    let tab = 0;

    if (props.isNsc && pendingAction && props.location) {
      if (props.location.actionName === DELIVERY_BANDS) {
        tab = 1;
      } else if (props.location.actionName === LEAD_TIMES) {
        tab = 3;
      } else if (props.location.actionName === CUPID_ID) {
        tab = 2;
      } else if (props.location.actionName === PAYMENT_THRESHOLD) {
        tab = 4;
      }
    }

    if (
      pendingAction &&
      props.location &&
      props.location.actionName === TERMS_AND_CONDITIONS &&
      props.user &&
      props.user.businessSettingsPage === -1
    ) {
      tab = 0;
      if (props.user.updateBusinessSettingsTab) {
        props.user.updateBusinessSettingsTab(0);
      }
      props.user.businessSettingsPage = 0;
    }

    return tab;
  }, [props.isNsc, pendingAction, props.location, props.user]);

  const selectedTabRef = useRef(initialTab);

  const fromPendingRef = useRef(
    pendingAction &&
    props.location &&
    props.location.actionName === TERMS_AND_CONDITIONS &&
    props.user &&
    (props.user.businessSettingsPage === -1 || props.user.businessSettingsPage === 0)
  );

  const hasInitialized = useRef(false);

  const userWithDefaults = useMemo(() => {
    if (!props.user) {
      return props.user;
    }

    if (props.user.pendingActions) {
      return props.user;
    }

    const userCopy = Object.create(Object.getPrototypeOf(props.user));
    Object.assign(userCopy, props.user, { pendingActions: [] });
    return userCopy;
  }, [props.user]);

  const filterDealers = useCallback(() => {
    const filteredDealers = [];
    if (props.dealerGroup) {
      const currentDealer = props.dealerGroup.find(
        (dealer) => dealer.commonId === props.user?.commonId
      );
      if (currentDealer) {
        if (
          props.user?.showAll &&
          (currentDealer.dealerType === "HUB" || currentDealer.dealerType === "MDLR")
        ) {
          return [...props.dealerGroup];
        } else {
          return [currentDealer];
        }
      }
    }
    return filteredDealers;
  }, [props.dealerGroup, props.user]);

  const filteredDealersRef = useRef(filterDealers());

  const handleTermsAndConditionsDownload = useCallback((termsandconditions) => {
    base64FileDownload(
      termsandconditions.file,
      "application/pdf",
      `${
        props.user?.market?.toUpperCase() +
        " " +
        props.t("TermsandConditionsHistory.documentName") +
        termsandconditions.version
      }.pdf`
    );
  }, [props.user, props.t]);

  const resetFromPending = useCallback(() => {
    fromPendingRef.current = false;
    setIsDealerAcceptingTerms(false);
    setRestrictDropdown(false);
  }, []);

  const addTermsAndConditionsPA = useCallback(async () => {
    try {
      if (props.hideOrShow) {
        props.hideOrShow(true);
      }
      await PendingActionClient.addPA(props.user, TERMS_AND_CONDITIONS);
    } catch (error) {
      console.error("Error adding PA:", error);
    } finally {
      if (props.hideOrShow) {
        props.hideOrShow(false);
      }
    }
  }, [props.hideOrShow, props.user]);

  const loadTermsAndConditionsHistory = useCallback(async () => {
    try {
      if (props.hideOrShow) {
        props.hideOrShow(true);
      }
      const data = await TermsAndConditionsClient.retrieveTermsAndConditionsHistory(props.user);

      setTermsAndConditionsHistory(data);
      setEnableUpload(!(data.length > 1));

      if (
        pendingAction &&
        props.location &&
        props.location.actionName === "TERMS_CONDITIONS" &&
        !props.isNsc &&
        props.user &&
        props.user.businessSettingsPage === 0 &&
        fromPendingRef.current
      ) {
        const tnC = data
          .slice()
          .reverse()
          .find((terms) => !terms.accepted);

        if (tnC) {
          setIsDealerAcceptingTerms(true);
          setRestrictDropdown(true);
          setAcceptTermsAndConditions(tnC);
        }
      }
    } catch (error) {
      console.error("Error loading terms and conditions:", error);
    } finally {
      if (props.hideOrShow) {
        props.hideOrShow(false);
      }
    }
  }, [props.hideOrShow, props.user, props.location, props.isNsc, pendingAction]);

  const alterComponentView = useCallback((termsAndConditions) => {
    const wasAcceptingTerms = isDealerAcceptingTerms;

    setIsDealerAcceptingTerms(prev => !prev);
    setAcceptTermsAndConditions(termsAndConditions);
    setRestrictDropdown(prev => !prev);

    if (wasAcceptingTerms) {
      setTimeout(() => {
        if (userWithDefaults?.pendingActions?.length === 0) {
          if (props.updateDealerGroupDetails) {
            props.updateDealerGroupDetails();
          }
        }

        if (fromPendingRef.current) {
          if (props.history) {
            props.history.push("/dashboard");
          }
        } else {
          loadTermsAndConditionsHistory();
        }
      }, 100);
    }
  }, [isDealerAcceptingTerms, userWithDefaults, props.updateDealerGroupDetails, props.history, loadTermsAndConditionsHistory]);

  const deleteTerms = useCallback(async (version) => {
    try {
      if (props.hideOrShow) {
        props.hideOrShow(true);
      }
      await TermsAndConditionsClient.deleteTermsAndConditions(props.user, version);

      addTermsAndConditionsPA();

      setTermsAndConditionsHistory(prevTerms => {
        const updatedTerms = prevTerms.filter(terms => terms.version !== version);
        setEnableUpload(!(updatedTerms.length > 1));
        return updatedTerms;
      });
    } catch (error) {
      console.error("Error deleting terms:", error);
    } finally {
      if (props.hideOrShow) {
        props.hideOrShow(false);
      }
    }
  }, [props.hideOrShow, props.user, addTermsAndConditionsPA]);

  useEffect(() => {
    if (hasInitialized.current) {
      return;
    }

    hasInitialized.current = true;
    setIsDataLoaded(true);
  }, []);

  useEffect(() => {
    if (isDataLoaded && !props.isNsc && hasInitialized.current) {
      loadTermsAndConditionsHistory();
    }
  }, [isDataLoaded, props.isNsc, loadTermsAndConditionsHistory]);

  if (!isDataLoaded) {
    return null;
  }

  return (
    <div>
      <HasPermissionTo
        perform={["dealerBusinessSettings:view"]}
        permissions={props.user?.permissions?.rolePermissions}
        render={() => (
          <DealerBusinessSettings
            {...props}
            user={userWithDefaults}
            startingTab={selectedTabRef.current}
            restrictDropdown={restrictDropdown}
            isDealerAcceptingTerms={isDealerAcceptingTerms}
            fromPending={fromPendingRef.current}
            resetFromPending={resetFromPending}
            termsAndConditions={acceptTermsAndConditions}
            alterComponentView={alterComponentView}
            loadTermsAndConditionsHistory={loadTermsAndConditionsHistory}
            handleTermsAndConditionsDownload={handleTermsAndConditionsDownload}
            termsAndConditionsHistory={termsAndConditionsHistory}
            filteredDealers={filteredDealersRef.current}
            isDealer={true}
          />
        )}
        noRender={() => (
          <NscBusinessSettings
            {...props}
            user={userWithDefaults}
            startingTab={selectedTabRef.current}
            restrictDropdown={restrictDropdown}
            handleTermsAndConditionsDownload={handleTermsAndConditionsDownload}
            isDealer={false}
            enableUpload={enableUpload}
            deleteTerms={deleteTerms}
            loadTermsAndConditionsHistory={loadTermsAndConditionsHistory}
            termsAndConditionsHistory={termsAndConditionsHistory}
          />
        )}
      />
    </div>
  );
}

export default withTranslation("emp")(BusinessSettings);
